import { getFormattedDate } from "@utils/CommonUtils";

/**
 * Has this record actually been updated since it was created?
 *
 * The API stamps `updatedOn` / `updatedBy` when a record is first written, so
 * every brand new row looked as though someone had already revised it — the
 * Updated columns showed the creation time and the creator's name. There is no
 * flag distinguishing the two, so a record counts as updated only once its
 * update stamp moves away from its creation stamp.
 */
const sameInstant = (a: any, b: any) =>
  String(a) === String(b) || getFormattedDate(a) === getFormattedDate(b);

/* One spelling: the payload answers with `updatedOn` / `updatedBy`. */
const updatedStamp = (record: any) => record?.updatedOn;
const createdStamp = (record: any) => record?.createdOn;

const updatedActor = (record: any) => record?.updatedBy;
const createdActor = (record: any) => record?.createdBy;

export const wasUpdated = (record: any): boolean => {
  const updated = updatedStamp(record);
  const created = createdStamp(record);

  /* Someone other than the creator is named as the last editor — that is an
     edit whether or not the stamp moved with it. Relying on the stamp alone
     hid the editor when the API recorded the name but left the time at its
     creation value. */
  const editor = String(updatedActor(record) ?? "").trim();
  const creator = String(createdActor(record) ?? "").trim();
  if (editor && editor.toLowerCase() !== creator.toLowerCase()) return true;

  if (!updated) return false;
  if (!created) return true;
  return !sameInstant(created, updated);
};

/** The Updated At cell — blank until the record is genuinely updated. */
export const updatedAtOf = (record: any): string =>
  wasUpdated(record) ? getFormattedDate(updatedStamp(record)) || "-" : "-";

/** The Updated By cell — blank until the record is genuinely updated. */
export const updatedByOf = (record: any): string =>
  wasUpdated(record) ? updatedActor(record) || "-" : "-";
